import { useState, useCallback, useRef, useEffect } from 'react'
import { formatPhoneNumber } from '../utils/formatPhone'
import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Popper,
  Fade,
  Divider,
  useTheme,
  alpha,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import PersonIcon from '@mui/icons-material/Person'
import PeopleIcon from '@mui/icons-material/People'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import CloseIcon from '@mui/icons-material/Close'
import HistoryIcon from '@mui/icons-material/History'
import { useNavigate } from 'react-router-dom'
import { searchApi, SearchResult } from '../services/api'

// Chip colors that are valid for MUI Chip component
type ChipColor = 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error' | 'default'

interface EntityConfig {
  icon: typeof PersonIcon
  color: ChipColor
  label: string | ((item: SearchResult) => string)
  getLink: (item: SearchResult) => string
  getTitle: (item: SearchResult) => string
  getSubtitle: (item: SearchResult) => string
}

// Helper to safely get string from data
const getString = (value: unknown): string => (typeof value === 'string' ? value : '')

const entityConfig: Record<string, EntityConfig> = {
  lead: {
    icon: PersonIcon,
    color: 'warning',
    label: 'Lead',
    getLink: (item) => `/leads?id=${item.id}`,
    getTitle: (item) => getString(item.data?.name) || getString(item.data?.email) || 'Unknown Lead',
    getSubtitle: (item) => getString(item.data?.email) || formatPhoneNumber(getString(item.data?.phone)),
  },
  client: {
    icon: PeopleIcon,
    color: 'primary',
    label: (item) => {
      const stage = item.data?.workflowStage
      if (stage === 'completed') return 'Completed'
      return 'Client'
    },
    getLink: (item) => `/clients/${item.id}`,
    getTitle: (item) => getString(item.data?.name) || getString(item.data?.email) || 'Unknown Client',
    getSubtitle: (item) => {
      const parts: string[] = []
      const email = getString(item.data?.email)
      const stage = getString(item.data?.workflowStage)
      if (email) parts.push(email)
      if (stage) parts.push(stage.replace(/_/g, ' '))
      return parts.join(' | ')
    },
  },
  task: {
    icon: TaskAltIcon,
    color: 'success',
    label: (item) => (item.data?.status === 'completed' ? 'Completed' : 'Task'),
    getLink: (item) => (item.data?.clientId ? `/clients/${getString(item.data.clientId)}` : '/tasks'),
    getTitle: (item) => getString(item.data?.title) || 'Untitled Task',
    getSubtitle: (item) => {
      const parts: string[] = []
      const clientName = getString(item.data?.clientName)
      const dueDate = item.data?.dueDate
      if (clientName) parts.push(clientName)
      if (dueDate && typeof dueDate === 'string') parts.push(`Due: ${new Date(dueDate).toLocaleDateString()}`)
      return parts.join(' | ')
    },
  },
  contact: {
    icon: ConnectWithoutContactIcon,
    color: 'info',
    label: 'Contact',
    // There is no route that opens a single contact — the detail panel is
    // opened from the table — so this lands on that client's network, the
    // same way a task result lands on its client rather than the task.
    getLink: (item) =>
      item.data?.clientId ? `/clients/${getString(item.data.clientId)}/networking` : '/networking',
    getTitle: (item) => {
      const full = getString(item.data?.fullName)
      if (full) return full
      const parts = [getString(item.data?.firstName), getString(item.data?.lastName)].filter(Boolean)
      return parts.join(' ') || 'Unknown Contact'
    },
    getSubtitle: (item) => {
      const parts: string[] = []
      const position = getString(item.data?.position)
      const company = getString(item.data?.company)
      // Whose network. The same person appears in several clients' networks as
      // separate records, so without this the rows are indistinguishable.
      const clientName = getString(item.data?.clientName)
      if (position) parts.push(position)
      if (company) parts.push(company)
      if (clientName) parts.push(clientName)
      return parts.join(' | ')
    },
  },
  jobapplication: {
    icon: WorkOutlineIcon,
    color: 'secondary',
    label: (item) => (item.data?.archived ? 'Archived' : 'Application'),
    getLink: (item) =>
      item.data?.clientId
        ? `/clients/${getString(item.data.clientId)}/opportunities`
        : '/opportunities',
    getTitle: (item) => getString(item.data?.jobTitle) || 'Untitled Application',
    getSubtitle: (item) => {
      const parts: string[] = []
      const company = getString(item.data?.company)
      const clientName = getString(item.data?.clientName)
      const location = getString(item.data?.location)
      if (company) parts.push(company)
      if (clientName) parts.push(clientName)
      if (location) parts.push(location)
      return parts.join(' | ')
    },
  },
}

const groupLabels: Record<string, string> = {
  leads: 'Leads',
  clients: 'Clients',
  tasks: 'Tasks',
  contacts: 'Side Door Contacts',
  jobApplications: 'Front Door',
}

/**
 * "5" when everything that matched is on screen, "5 of 36" when it is not.
 * Each type is capped server-side, and a capped list that reads a plain "5" is
 * indistinguishable from an exhausted one — which is the confusion that hid
 * job applications entirely before MWA-336.
 */
const groupCountLabel = (shown: number, total?: number) =>
  typeof total === 'number' && total > shown ? `${shown} of ${total}` : `${shown}`

function debounce<T extends (...args: Parameters<T>) => void>(fn: T, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}

const LISTBOX_ID = 'global-search-listbox'

/** One navigable row, in the order it is read on screen. */
type OptionEntry =
  | { kind: 'recent'; domId: string; term: string }
  | { kind: 'result'; domId: string; item: SearchResult }

const MAX_RECENT_SEARCHES = 5
const RECENT_SEARCHES_KEY = 'missfit_recent_searches'

export default function GlobalSearchBar() {
  const theme = useTheme()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  // Per-type match counts. Each type is capped server-side (MWA-336), so the
  // number of rows on screen is not the number that matched. Keyed by the same
  // group names as groupedResults below.
  const [groupedTotals, setGroupedTotals] = useState<Record<string, number> | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [focused, setFocused] = useState(false)
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  // Which option the keyboard is on. -1 means the caret is still in the field
  // and nothing is highlighted, which is the state a fresh query returns to.
  const [activeIndex, setActiveIndex] = useState(-1)

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(RECENT_SEARCHES_KEY)
      if (saved) {
        setRecentSearches(JSON.parse(saved))
      }
    } catch {
      // Ignore localStorage errors
    }
  }, [])

  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform)
  const shortcutLabel = isMac ? '⌘K' : 'Ctrl+K'

  const hasSearched = query.trim().length >= 2 && !loading
  const showNoResults = hasSearched && results.length === 0 && !error
  const showRecentSearches = focused && query.trim().length === 0 && recentSearches.length > 0
  const open = Boolean(anchorEl) && (results.length > 0 || !!error || showNoResults || showRecentSearches)

  const saveRecentSearch = useCallback((searchQuery: string) => {
    if (!searchQuery?.trim()) return

    const trimmedQuery = searchQuery.trim()

    setRecentSearches((prevSearches) => {
      // Don't save if it's already the most recent search
      if (prevSearches[0] === trimmedQuery) return prevSearches

      // Remove duplicate if exists and add to front
      const updatedSearches = [trimmedQuery, ...prevSearches.filter((s) => s !== trimmedQuery)].slice(
        0,
        MAX_RECENT_SEARCHES
      )

      // Save to localStorage
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updatedSearches))
      } catch {
        // Ignore localStorage errors
      }

      return updatedSearches
    })
  }, [])

  const performSearch = useCallback(
    async (searchQuery: string, shouldSave = true) => {
      if (!searchQuery || searchQuery.trim().length < 2) {
        setResults([])
        setGroupedTotals(undefined)
        setError(null)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const response = await searchApi.globalSearch(searchQuery.trim(), 20)
        setResults(response.results || [])
        setGroupedTotals(response.groupedTotals)

        // Save to recent searches after successful search
        if (shouldSave && response.results?.length > 0) {
          saveRecentSearch(searchQuery)
        }
      } catch (err) {
        console.error('[GlobalSearch] Error:', err)
        if (err instanceof Error && (err.message.includes('503') || err.message.includes('unavailable'))) {
          setError('Search is temporarily unavailable')
        } else {
          setError('Search failed')
        }
        setResults([])
        setGroupedTotals(undefined)
      } finally {
        setLoading(false)
      }
    },
    [saveRecentSearch]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(debounce(performSearch, 300), [performSearch])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setQuery(value)
    debouncedSearch(value)
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true)
    setAnchorEl(e.currentTarget)
  }

  const handleClose = () => {
    setFocused(false)
    setAnchorEl(null)
  }

  const handleClear = () => {
    setQuery('')
    setResults([])
    setError(null)
    inputRef.current?.focus()
  }

  const handleResultClick = (item: SearchResult) => {
    const config = entityConfig[item.entityType]
    if (config) {
      const link = config.getLink(item)
      navigate(link)
      handleClose()
      setQuery('')
      setResults([])
    }
  }

  const handleRecentSearchClick = (searchTerm: string) => {
    setQuery(searchTerm)
    // Don't save again since it's already a recent search
    performSearch(searchTerm, false)
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
      }
      if (e.key === 'Escape' && focused) {
        handleClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [focused])

  const groupedResults = {
    leads: results.filter((r) => r.entityType === 'lead'),
    clients: results.filter((r) => r.entityType === 'client'),
    tasks: results.filter((r) => r.entityType === 'task'),
    contacts: results.filter((r) => r.entityType === 'contact'),
    jobApplications: results.filter((r) => r.entityType === 'jobapplication'),
  }

  // The options flattened into the order they are read on screen, so Down goes
  // from the last lead to the first client rather than stopping at the group
  // heading. Object.entries follows the literal above, which is render order.
  const options: OptionEntry[] = showRecentSearches
    ? recentSearches.map((term, index) => ({
        kind: 'recent',
        domId: `${LISTBOX_ID}-recent-${index}`,
        term,
      }))
    : Object.values(groupedResults)
        .flat()
        .filter((item) => entityConfig[item.entityType])
        .map((item) => ({ kind: 'result', domId: `${LISTBOX_ID}-${item.id}`, item }))

  const activeOptionId = activeIndex >= 0 ? options[activeIndex]?.domId : undefined

  // A new query means a new list; keeping the old index would highlight an
  // unrelated row, or one that no longer exists.
  useEffect(() => {
    setActiveIndex(-1)
  }, [query, results, showRecentSearches])

  // Keyboard focus never leaves the field — that is the combobox pattern — so
  // the highlighted option has to be scrolled to deliberately.
  useEffect(() => {
    if (!activeOptionId) return
    document.getElementById(activeOptionId)?.scrollIntoView({ block: 'nearest' })
  }, [activeOptionId])

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || options.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveIndex((i) => (i + 1) % options.length)
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIndex((i) => (i <= 0 ? options.length - 1 : i - 1))
        break
      case 'Home':
        e.preventDefault()
        setActiveIndex(0)
        break
      case 'End':
        e.preventDefault()
        setActiveIndex(options.length - 1)
        break
      case 'Enter': {
        // Enter with nothing highlighted belongs to the field, not the list.
        if (activeIndex < 0) return
        const entry = options[activeIndex]
        if (!entry) return
        e.preventDefault()
        if (entry.kind === 'recent') handleRecentSearchClick(entry.term)
        else handleResultClick(entry.item)
        break
      }
    }
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: 'relative', width: '100%', maxWidth: { xs: '100%', sm: 280, md: 400 } }}>
        <TextField
          inputRef={inputRef}
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleInputKeyDown}
          placeholder="Search clients, contacts, applications..."
          size="small"
          fullWidth
          inputProps={{
            role: 'combobox',
            'aria-expanded': open,
            'aria-controls': open ? LISTBOX_ID : undefined,
            'aria-activedescendant': activeOptionId,
            'aria-autocomplete': 'list',
            'aria-label': 'Search clients, contacts and applications',
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: alpha(theme.palette.background.paper, 0.15),
              borderRadius: 2,
              transition: 'all 0.2s',
              color: 'white',
              '& fieldset': {
                borderColor: 'rgba(255,255,255,0.2)',
              },
              '&:hover': {
                backgroundColor: alpha(theme.palette.background.paper, 0.25),
                '& fieldset': {
                  borderColor: 'rgba(255,255,255,0.3)',
                },
              },
              '&.Mui-focused': {
                backgroundColor: alpha(theme.palette.background.paper, 0.3),
                '& fieldset': {
                  borderColor: 'rgba(255,255,255,0.5)',
                },
              },
            },
            '& .MuiOutlinedInput-input': {
              py: 1,
              fontSize: '0.875rem',
              '&::placeholder': {
                color: 'rgba(255,255,255,0.6)',
                opacity: 1,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {loading ? (
                  <CircularProgress size={18} sx={{ color: 'rgba(255,255,255,0.7)' }} />
                ) : query ? (
                  <CloseIcon
                    sx={{ fontSize: 18, cursor: 'pointer', color: 'rgba(255,255,255,0.7)' }}
                    onClick={handleClear}
                  />
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255,255,255,0.5)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      borderRadius: 0.5,
                      px: 0.5,
                      fontSize: '0.7rem',
                      display: { xs: 'none', md: 'block' },
                    }}
                  >
                    {shortcutLabel}
                  </Typography>
                )}
              </InputAdornment>
            ),
          }}
        />

        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          transition
          style={{
            zIndex: theme.zIndex.modal + 1,
            width: anchorEl?.offsetWidth || 300,
            minWidth: 300,
            maxWidth: '95vw',
          }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={200}>
              <Paper
                elevation={8}
                sx={{
                  mt: 1,
                  maxHeight: 450,
                  overflow: 'auto',
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                {error && !loading && (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="error">
                      {error}
                    </Typography>
                  </Box>
                )}

                {!loading && !error && results.length === 0 && query.length >= 2 && (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No results found for "{query}"
                    </Typography>
                  </Box>
                )}

                {showRecentSearches && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        px: 2,
                        py: 0.5,
                        display: 'block',
                        color: 'text.secondary',
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      Recent Searches
                    </Typography>
                    <List dense sx={{ py: 0 }} id={LISTBOX_ID} role="listbox" aria-label="Recent searches">
                      {recentSearches.map((searchTerm, index) => (
                        <ListItem
                          key={`recent-${index}`}
                          id={`${LISTBOX_ID}-recent-${index}`}
                          role="option"
                          aria-selected={activeIndex === index}
                          onClick={() => handleRecentSearchClick(searchTerm)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor:
                              activeIndex === index
                                ? alpha(theme.palette.primary.main, 0.12)
                                : 'transparent',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <HistoryIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText primary={searchTerm} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {!loading && !error && results.length > 0 && (
                  <List dense sx={{ py: 0 }} id={LISTBOX_ID} role="listbox" aria-label="Search results">
                    {Object.entries(groupedResults).map(([group, items]) => {
                      if (items.length === 0) return null

                      const groupLabel = groupLabels[group] || group

                      return (
                        <Box key={group} role="group" aria-label={groupLabel}>
                          <Typography
                            variant="caption"
                            sx={{
                              px: 2,
                              py: 0.5,
                              display: 'block',
                              color: 'text.secondary',
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                            }}
                          >
                            {groupLabel} ({groupCountLabel(items.length, groupedTotals?.[group])})
                          </Typography>
                          {items.map((item) => {
                            const config = entityConfig[item.entityType]
                            if (!config) return null

                            const Icon = config.icon
                            const label = typeof config.label === 'function' ? config.label(item) : config.label
                            const title = config.getTitle(item)
                            const subtitle = config.getSubtitle(item)

                            const optionIndex = options.findIndex(
                              (o) => o.kind === 'result' && o.item.id === item.id
                            )

                            return (
                              <ListItem
                                key={item.id}
                                id={`${LISTBOX_ID}-${item.id}`}
                                role="option"
                                aria-selected={activeIndex === optionIndex}
                                onClick={() => handleResultClick(item)}
                                sx={{
                                  cursor: 'pointer',
                                  backgroundColor:
                                    activeIndex === optionIndex
                                      ? alpha(theme.palette.primary.main, 0.12)
                                      : 'transparent',
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                  },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Icon color={config.color === 'default' ? 'inherit' : config.color} fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                        {title}
                                      </Typography>
                                      <Chip
                                        label={label}
                                        size="small"
                                        color={config.color}
                                        variant="outlined"
                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                      />
                                    </Box>
                                  }
                                  secondary={subtitle}
                                  secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
                                />
                              </ListItem>
                            )
                          })}
                          <Divider />
                        </Box>
                      )
                    })}
                  </List>
                )}
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}
